import { create } from "zustand";
// NOTE: Circular dependency with validationState.ts is intentional and safe —
// both Zustand stores are lazily initialised so runtime access always resolves.
import {
  useValidationStateStore,
  runClientSideValidations,
} from "./validationState";
import {
  applyPropertyEdit,
  applyStepListChange,
  clearItemTreeChanges,
  clearProcessTreeChanges,
  getProcessAtPath,
  parseBomPath,
  navigateBomTree,
  navigateBomTreeWithResult,
  processTreeHasChanges,
  removeStepListChange,
  updateProcessAtPath,
} from "../../utils/conditions";
import { getLineEntry } from "../../utils/bomSlots";

// ============================================================================
// Pure Utility Functions (no store access)
// ============================================================================

/**
 * Get the item identifier (UUID if available, otherwise part number)
 * Works with ArenaOnlyItem, Item, and Process types
 */
export const getItemIdentifier = (
  item: ArenaOnlyItem | Item | Process
): string => {
  return "uuid" in item && typeof item.uuid === "string"
    ? item.uuid
    : item.arena_part_number;
};

/**
 * Get item metadata for tracking
 */
export const getItemMetadata = (
  item: ArenaOnlyItem | Item | Process
): { identifier: string; partNumber: string; uuid?: string } => {
  return {
    identifier: getItemIdentifier(item),
    partNumber: item.arena_part_number,
    uuid:
      "uuid" in item && typeof item.uuid === "string" ? item.uuid : undefined,
  };
};

/**
 * Type guard to check if an item is of type Item (has uuid)
 */
export const isItem = (item: ArenaOnlyItem | Item): item is Item => {
  return "uuid" in item && typeof item.uuid === "string";
};

// ============================================================================
// Change Tracking Types
// ============================================================================

/**
 * Change target using path-based approach
 * The path is the bom-item URL parameter format: "0>1>2" (absolute BOM indices)
 * Empty string means the root condition item
 */
export type ChangeTarget =
  | {
      type: "attribute";
      path: string;
      propertyName: string;
    }
  | {
      type: "parameter";
      path: string;
      processPath: number[];
      propertyName: string;
    }
  | {
      type: "bom_quantity";
      path: string;
      bomIndex: number;
    }
  | {
      type: "bom";
      path: string;
      bomIndex: number;
      addedItemUuid: string;
    }
  | {
      type: "processes";
      path: string;
      processIndex: number;
      addedProcessUuid: string;
    }
  | {
      // Add/delete of a nested step. `processPath` points at the PARENT
      // process; `stepIndex` indexes into that parent's `steps`.
      type: "steps";
      path: string;
      processPath: number[];
      stepIndex: number;
      addedProcessUuid: string;
    };

/**
 * Shared helper for building attribute ChangeTargets.
 * Use this anywhere you need to track/read attribute changes via `trackChange/getChange`.
 */
export const makeAttributeChangeTarget = (
  path: string,
  propertyName: string
): ChangeTarget => ({
  type: "attribute",
  path,
  propertyName,
});

export type ChangeOperation = "add" | "update" | "delete";

export type Change = {
  target: ChangeTarget;
  operation: ChangeOperation;
  currentValue?: ValueType;
  previousValue?: ValueType;
  timestamp: number;
};

// ============================================================================
// Specification State Store
// ============================================================================

interface SpecificationState {
  condition: FormspecCondition | null;
  baselineConditionName: string | null;
  successMessage: string | null;
  errorMessage: string | null;
  /** Condition whose spec is being modified; locks every other condition. */
  activeModifyConditionId: number | null;
  modifyStatus:
    | "modifying"
    | "queued"
    | "submitted"
    | "failed"
    | "cancelled"
    | null;

  setCondition: (condition: FormspecCondition | null) => void;
  trackChange: (change: Change) => void;
  removeChange: (target: ChangeTarget) => void;
  clearChanges: () => void;
  appendBomItem: (path: string, item: BOMItem) => void;
  appendProcess: (path: string, process: ProcessWithSteps) => void;
  /** Append a step to the process at `processPath` (under BOM `path`). */
  appendStep: (
    path: string,
    processPath: number[],
    step: ProcessWithSteps
  ) => void;
  replaceBomItem: (path: string, bomIndex: number, item: ItemWithBOM) => void;
  /** Swap the condition's own item (and its BOM and processes) for `item`. */
  replaceRootItem: (item: ItemWithBOM) => void;
  updateConditionName: (name: string) => void;
  checkForUnsavedChanges: () => boolean;
  isChanged: (target: ChangeTarget) => boolean;
  getChange: (target: ChangeTarget) => Change | null;
  setSuccessMessage: (message: string) => void;
  clearSuccessMessage: () => void;
  setErrorMessage: (message: string) => void;
  clearErrorMessage: () => void;
  setModifyState: (
    conditionId: number | null,
    status: SpecificationState["modifyStatus"]
  ) => void;
}

export const useSpecificationStateStore = create<SpecificationState>(
  (set, get) => ({
    condition: null,
    baselineConditionName: null,
    successMessage: null,
    errorMessage: null,
    activeModifyConditionId: null,
    modifyStatus: null,

    setCondition: (condition) => {
      if (!condition) {
        set({ condition: null, baselineConditionName: null });
        return;
      }

      // Helper to initialize ItemWithBOM recursively (also handles BOMItem and FormspecCondition)
      const initializeItemWithBOM = <T extends ItemWithBOM>(item: T): T => {
        return {
          ...item,
          bom: (item.bom || []).map(initializeItemWithBOM),
          processes: (item.processes || []).map(clearProcessTreeChanges),
          item_changes: [],
          bom_changes: [],
          process_changes: [],
        } as T;
      };

      const initializedCondition = initializeItemWithBOM(condition);
      const normalizedName = initializedCondition.name || "";
      set({
        condition: {
          ...initializedCondition,
          name: normalizedName,
        },
        baselineConditionName: normalizedName,
      });
    },

    trackChange: (change) => {
      set((state) => {
        if (!state.condition) {
          return state;
        }

        const { target, operation } = change;
        const indices = parseBomPath(target.path);

        // Helper to update indices after an item is deleted
        const updateIndicesAfterDeletion = (
          changes: Array<{ index: number }>,
          deletedIndex: number
        ) => {
          changes.forEach((change) => {
            if (change.index > deletedIndex) {
              change.index -= 1;
            }
          });
        };

        // Helper to handle deleting an item that was previously added
        const handleDeleteAddedItem = <T extends { index: number }>(
          changes: T[],
          items: any[],
          targetIndex: number,
          isAddedPredicate: (change: T) => boolean
        ): { changes: T[]; items: any[]; wasAdded: boolean } => {
          const existingAddIndex = changes.findIndex(
            (c) => c.index === targetIndex && isAddedPredicate(c)
          );

          if (existingAddIndex >= 0) {
            // Remove from changes array
            const updatedChanges = [...changes];
            updatedChanges.splice(existingAddIndex, 1);
            // Remove from actual items array
            const updatedItems = items.filter((_, i) => i !== targetIndex);
            // Update indices
            updateIndicesAfterDeletion(updatedChanges, targetIndex);
            return {
              changes: updatedChanges,
              items: updatedItems,
              wasAdded: true,
            };
          }

          return { changes, items, wasAdded: false };
        };

        // Function to apply at the target depth
        const applyChange = (item: ItemWithBOM): ItemWithBOM => {
          const updated = { ...item };

          if (target.type === "attribute" || target.type === "parameter") {
            // Shared helper handles both attribute and parameter edits,
            // including walking nested processPath for parameters.
            applyPropertyEdit(
              updated,
              target,
              change.previousValue,
              change.currentValue,
              operation
            );
          } else if (target.type === "bom" || target.type === "bom_quantity") {
            // Update bom_changes for BOM changes
            const bomChanges = [...(updated.bom_changes || [])];

            // Only look for existing changes when updating quantity
            // For bom add/delete, always append a new change
            if (target.type === "bom_quantity") {
              // Find existing change for this index (including pending items with is_added: true)
              // We exclude is_deleted items since those shouldn't have quantity changes
              const existingIndex = bomChanges.findIndex(
                (c) => c.index === target.bomIndex && !c.is_deleted
              );

              // Check if the new quantity matches the original quantity
              if (change.currentValue === change.previousValue) {
                // Only remove the change if it's not a pending item (is_added: true)
                // Pending items must keep their change record to maintain is_added status
                if (existingIndex >= 0) {
                  const existingChange = bomChanges[existingIndex];
                  if (existingChange.is_added) {
                    // For pending items, remove quantity fields but keep the change
                    bomChanges[existingIndex] = {
                      ...existingChange,
                      old_quantity: undefined,
                      new_quantity: undefined,
                    };
                  } else {
                    // For existing items, remove the change entirely
                    bomChanges.splice(existingIndex, 1);
                  }
                }
              } else {
                const listChange: ListChange =
                  existingIndex >= 0
                    ? {
                        // For existing changes, spread to preserve all fields (including is_added, is_deleted)
                        ...bomChanges[existingIndex],
                        // Only update the quantity fields
                        old_quantity: change.previousValue as number,
                        new_quantity: change.currentValue as number,
                      }
                    : {
                        // For new quantity changes (no existing change at this index)
                        index: target.bomIndex,
                        old_quantity: change.previousValue as number,
                        new_quantity: change.currentValue as number,
                        is_added: operation === "add",
                        is_deleted: operation === "delete",
                      };

                if (existingIndex >= 0) {
                  bomChanges[existingIndex] = listChange;
                } else {
                  bomChanges.push(listChange);
                }
              }
            } else {
              // For bom type (add/delete)
              if (operation === "delete") {
                const result = handleDeleteAddedItem(
                  bomChanges,
                  updated.bom,
                  target.bomIndex,
                  (c) => c.is_added === true
                );

                if (result.wasAdded) {
                  updated.bom = result.items;
                  bomChanges.splice(0, bomChanges.length, ...result.changes);
                } else {
                  const listChange: ListChange = {
                    index: target.bomIndex,
                    is_added: false,
                    is_deleted: true,
                  };
                  bomChanges.push(listChange);
                }
              } else {
                // For add operation, always append
                const listChange: ListChange = {
                  index: target.bomIndex,
                  is_added: true,
                  is_deleted: false,
                };
                bomChanges.push(listChange);
              }
            }

            updated.bom_changes = bomChanges;
          } else if (target.type === "processes") {
            // Update process_changes for process list changes
            const processChanges = [...(updated.process_changes || [])];

            if (operation === "delete") {
              const result = handleDeleteAddedItem(
                processChanges,
                updated.processes || [],
                target.processIndex,
                (c) => c.is_added === true
              );

              if (result.wasAdded) {
                updated.processes = result.items;
                processChanges.splice(
                  0,
                  processChanges.length,
                  ...result.changes
                );
              } else {
                const existingIndex = processChanges.findIndex(
                  (c) => c.index === target.processIndex
                );

                const listChange: ListChange = {
                  index: target.processIndex,
                  is_added: false,
                  is_deleted: true,
                };

                if (existingIndex >= 0) {
                  processChanges[existingIndex] = listChange;
                } else {
                  processChanges.push(listChange);
                }
              }
            } else {
              // For add operation, always append
              const listChange: ListChange = {
                index: target.processIndex,
                is_added: true,
                is_deleted: false,
              };
              processChanges.push(listChange);
            }

            updated.process_changes = processChanges;
          } else if (target.type === "steps") {
            if (operation === "add" || operation === "delete") {
              const nextProcesses = updateProcessAtPath(
                updated.processes || [],
                target.processPath,
                (parent) =>
                  applyStepListChange(parent, target.stepIndex, operation)
              );
              if (nextProcesses) {
                updated.processes = nextProcesses;
              }
            }
          }

          return updated;
        };

        const updatedCondition = navigateBomTree(
          state.condition,
          indices,
          applyChange
        ) as FormspecCondition;

        return { condition: updatedCondition };
      });

      // Run client-side validations against the freshly-updated condition
      const { condition: updatedCondition } = get();
      const { constraints } = useValidationStateStore.getState();
      if (updatedCondition && constraints) {
        runClientSideValidations({
          condition: updatedCondition,
          constraints,
          path: change.target.path,
          getChange: get().getChange,
        });
      }
    },

    removeChange: (target) => {
      set((state) => {
        if (!state.condition) {
          return state;
        }

        const indices = parseBomPath(target.path);

        // Function to apply at the target depth
        const removeChange = (item: ItemWithBOM): ItemWithBOM => {
          const updated = { ...item };

          if (target.type === "attribute") {
            const changes = updated.item_changes || [];
            const removed = changes.find((c) => c.name === target.propertyName);
            updated.item_changes = changes.filter(
              (c) => c.name !== target.propertyName
            );
            // A pending-added attribute has no saved baseline, so its value
            // must also be stripped from item.attributes — otherwise the row
            // lingers as if it were a pre-existing attribute. Existing
            // (non-added) attributes keep their value so an undo can restore
            // them.
            if (
              removed?.is_added &&
              updated.item?.attributes &&
              target.propertyName in updated.item.attributes
            ) {
              const { [target.propertyName]: _omit, ...rest } =
                updated.item.attributes;
              updated.item = { ...updated.item, attributes: rest };
            }
          } else if (target.type === "bom" || target.type === "bom_quantity") {
            updated.bom_changes = (updated.bom_changes || []).filter(
              (c) => c.index !== target.bomIndex
            );
          } else if (target.type === "processes") {
            updated.process_changes = (updated.process_changes || []).filter(
              (c) => c.index !== target.processIndex
            );
          } else if (target.type === "steps") {
            const nextProcesses = updateProcessAtPath(
              updated.processes || [],
              target.processPath,
              (parent) => removeStepListChange(parent, target.stepIndex)
            );
            if (nextProcesses) {
              updated.processes = nextProcesses;
            }
          } else if (target.type === "parameter") {
            // Walk processPath and clone the chain so we can filter out the
            // named property change at the leaf without mutating prior state.
            if (!updated.processes || target.processPath.length === 0) {
              return updated;
            }

            const clonedProcesses = [...updated.processes];
            let currentList: ProcessWithSteps[] = clonedProcesses;
            for (
              let depth = 0;
              depth < target.processPath.length - 1;
              depth += 1
            ) {
              const idx = target.processPath[depth];
              if (idx < 0 || idx >= currentList.length) {
                return updated;
              }
              const clonedEntry: ProcessWithSteps = {
                ...currentList[idx],
                steps: [...(currentList[idx].steps || [])],
              };
              currentList[idx] = clonedEntry;
              currentList = clonedEntry.steps;
            }

            const leafIndex = target.processPath[target.processPath.length - 1];
            if (leafIndex < 0 || leafIndex >= currentList.length) {
              return updated;
            }

            const targetProcess = { ...currentList[leafIndex] };
            const existingChanges = targetProcess.process_changes || [];
            const removed = existingChanges.find(
              (c) => c.name === target.propertyName
            );
            targetProcess.process_changes = existingChanges.filter(
              (c) => c.name !== target.propertyName
            );
            // Mirror the attribute branch: a pending-added parameter has no
            // saved baseline, so strip its value from process.parameters too,
            // otherwise it lingers as if it were a pre-existing parameter.
            if (
              removed?.is_added &&
              targetProcess.process?.parameters &&
              target.propertyName in targetProcess.process.parameters
            ) {
              const { [target.propertyName]: _omit, ...rest } =
                targetProcess.process.parameters;
              targetProcess.process = {
                ...targetProcess.process,
                parameters: rest,
              };
            }
            currentList[leafIndex] = targetProcess;
            updated.processes = clonedProcesses;
          }

          return updated;
        };

        const updatedCondition = navigateBomTree(
          state.condition,
          indices,
          removeChange
        ) as FormspecCondition;

        return { condition: updatedCondition };
      });

      // Run client-side validations against the freshly-updated condition
      const { condition: updatedCondition } = get();
      const { constraints } = useValidationStateStore.getState();
      if (updatedCondition && constraints) {
        runClientSideValidations({
          condition: updatedCondition,
          constraints,
          path: target.path,
          getChange: get().getChange,
        });
      }
    },

    clearChanges: () => {
      set((state) => {
        if (!state.condition) {
          return state;
        }

        // Uses the shared helper, which also clears nested
        // ProcessWithSteps.process_changes and step_changes. The previous
        // inline implementation missed those, leaving stale process-level
        // changes after a user confirmed "discard unsaved changes".
        return {
          condition: {
            ...clearItemTreeChanges(state.condition),
            name: state.baselineConditionName ?? state.condition.name,
          },
        };
      });
    },

    checkForUnsavedChanges: () => {
      const { condition } = get();
      if (!condition) return false;

      if (condition.name !== get().baselineConditionName) {
        return true;
      }

      // Helper to check if an item has changes
      const hasChanges = (itemWithBOM: ItemWithBOM): boolean => {
        if (
          itemWithBOM.item_replaced ||
          (itemWithBOM.item_changes && itemWithBOM.item_changes.length > 0) ||
          (itemWithBOM.bom_changes && itemWithBOM.bom_changes.length > 0) ||
          (itemWithBOM.process_changes &&
            itemWithBOM.process_changes.length > 0)
        ) {
          return true;
        }

        // Parameter edits live on each process (and its nested steps)
        if ((itemWithBOM.processes || []).some(processTreeHasChanges)) {
          return true;
        }

        // Recursively check BOM children
        return itemWithBOM.bom.some(hasChanges);
      };

      return hasChanges(condition);
    },

    isChanged: (target) => {
      const { condition } = get();
      if (!condition) return false;

      const indices = parseBomPath(target.path);

      const checkForChange = (item: ItemWithBOM): boolean => {
        if (target.type === "attribute") {
          return (item.item_changes || []).some(
            (c) => c.name === target.propertyName
          );
        } else if (target.type === "bom" || target.type === "bom_quantity") {
          return (item.bom_changes || []).some(
            (c) => c.index === target.bomIndex
          );
        } else if (target.type === "processes") {
          return (item.process_changes || []).some(
            (c) => c.index === target.processIndex
          );
        } else if (target.type === "steps") {
          if (!item.processes) return false;
          const location = getProcessAtPath(item.processes, target.processPath);
          if (!location) return false;
          return (location.list[location.index].step_changes || []).some(
            (c) => c.index === target.stepIndex
          );
        } else if (target.type === "parameter") {
          // Walk processPath (may be nested under another process's steps)
          if (!item.processes) return false;
          const location = getProcessAtPath(item.processes, target.processPath);
          if (!location) return false;
          const targetProcess = location.list[location.index];
          return (targetProcess.process_changes || []).some(
            (c) => c.name === target.propertyName
          );
        }
        return false;
      };

      return navigateBomTreeWithResult(
        condition,
        indices,
        checkForChange,
        false
      );
    },

    getChange: (target) => {
      const { condition } = get();
      if (!condition) return null;

      const indices = parseBomPath(target.path);

      // Helper to build Change object from found change data
      const buildChangeObject = (
        changeData: { is_added?: boolean; is_deleted?: boolean },
        currentValue: any,
        previousValue: any
      ): Change => ({
        target,
        operation: changeData.is_added
          ? "add"
          : changeData.is_deleted
          ? "delete"
          : "update",
        currentValue,
        previousValue,
        timestamp: Date.now(),
      });

      const extractChange = (item: ItemWithBOM): Change | null => {
        // Attribute changes
        if (target.type === "attribute") {
          const found = (item.item_changes || []).find(
            (c) => c.name === target.propertyName
          );

          return found
            ? buildChangeObject(found, found.new_value, found.old_value)
            : null;
        }

        // BOM changes
        if (target.type === "bom" || target.type === "bom_quantity") {
          const change = (item.bom_changes || []).find(
            (c) => c.index === target.bomIndex
          );
          return change
            ? buildChangeObject(
                change,
                change.new_quantity,
                change.old_quantity
              )
            : null;
        }

        // Process changes
        if (target.type === "processes") {
          const change = (item.process_changes || []).find(
            (c) => c.index === target.processIndex
          );
          return change
            ? buildChangeObject(change, undefined, undefined)
            : null;
        }

        // Step list changes (nested under a parent process)
        if (target.type === "steps") {
          if (!item.processes) return null;
          const location = getProcessAtPath(item.processes, target.processPath);
          if (!location) return null;
          const change = (
            location.list[location.index].step_changes || []
          ).find((c) => c.index === target.stepIndex);
          return change
            ? buildChangeObject(change, undefined, undefined)
            : null;
        }

        // Parameter changes
        if (target.type === "parameter") {
          if (!item.processes) return null;
          const location = getProcessAtPath(item.processes, target.processPath);
          if (!location) return null;
          const targetProcess = location.list[location.index];
          const change = (targetProcess.process_changes || []).find(
            (c) => c.name === target.propertyName
          );
          return change
            ? buildChangeObject(change, change.new_value, change.old_value)
            : null;
        }

        return null;
      };

      return navigateBomTreeWithResult(condition, indices, extractChange, null);
    },

    setSuccessMessage: (message) => {
      set({ successMessage: message, errorMessage: null });
    },

    clearSuccessMessage: () => {
      set({ successMessage: null });
    },

    setErrorMessage: (message) => {
      set({ errorMessage: message, successMessage: null });
    },

    clearErrorMessage: () => {
      set({ errorMessage: null });
    },

    setModifyState: (conditionId, status) => {
      set({ activeModifyConditionId: conditionId, modifyStatus: status });
    },

    updateConditionName: (name) => {
      set((state) => {
        if (!state.condition) {
          return state;
        }

        return {
          condition: {
            ...state.condition,
            name,
          },
        };
      });
    },

    appendBomItem: (path, item) => {
      set((state) => {
        if (!state.condition) {
          return state;
        }

        const indices = parseBomPath(path);
        const updatedCondition = navigateBomTree(
          state.condition,
          indices,
          (target) => ({
            ...target,
            bom: [...target.bom, item] as BOMItem[],
          })
        ) as FormspecCondition;

        return { condition: updatedCondition };
      });
    },

    appendProcess: (path, process) => {
      set((state) => {
        if (!state.condition) return state;

        const indices = parseBomPath(path);
        const updatedCondition = navigateBomTree(
          state.condition,
          indices,
          (target) => ({
            ...target,
            processes: [...(target.processes || []), process],
          })
        ) as FormspecCondition;

        return { condition: updatedCondition };
      });
    },

    appendStep: (path, processPath, step) => {
      set((state) => {
        if (!state.condition) return state;

        const indices = parseBomPath(path);
        const updatedCondition = navigateBomTree(
          state.condition,
          indices,
          (target) => {
            const nextProcesses = updateProcessAtPath(
              target.processes || [],
              processPath,
              (parent) => ({
                ...parent,
                steps: [...(parent.steps || []), step],
              })
            );
            return nextProcesses
              ? { ...target, processes: nextProcesses }
              : target;
          }
        ) as FormspecCondition;

        return { condition: updatedCondition };
      });
    },

    replaceBomItem: (path, bomIndex, item) => {
      set((state) => {
        if (!state.condition) return state;

        const indices = parseBomPath(path);
        const updatedCondition = navigateBomTree(
          state.condition,
          indices,
          (target) => {
            if (!target.bom || bomIndex >= target.bom.length) {
              return target;
            }

            const updatedBom = [...target.bom];
            const existing = target.bom[bomIndex];
            const existingQuantity = existing.quantity;
            const lineEntry = getLineEntry(target, existing);
            const sameType =
              isItem(existing.item) &&
              isItem(item.item) &&
              existing.item.item_type?.uuid === item.item.item_type?.uuid;
            updatedBom[bomIndex] = {
              ...item,
              quantity: existingQuantity,
              line_number: existing.line_number,
              ...(lineEntry !== null && sameType
                ? {
                    line_number: lineEntry,
                    hint: existing.hint,
                    slot_entry: true,
                  }
                : {}),
            } as BOMItem;

            const updatedChanges = [...(target.bom_changes || [])];
            const existingChangeIndex = updatedChanges.findIndex(
              (change) => change.index === bomIndex
            );

            const replacementChange: ListChange = {
              index: bomIndex,
              is_added: true,
              is_deleted: false,
              added_item_uuid: "uuid" in item.item ? item.item.uuid : undefined,
              added_item_part_number: item.item.arena_part_number,
              added_item_quantity: existingQuantity,
            };

            if (existingChangeIndex >= 0) {
              updatedChanges[existingChangeIndex] = replacementChange;
            } else {
              updatedChanges.push(replacementChange);
            }

            return {
              ...target,
              bom: updatedBom as BOMItem[],
              bom_changes: updatedChanges,
            };
          }
        ) as FormspecCondition;

        return { condition: updatedCondition };
      });
    },

    replaceRootItem: (item) => {
      set((state) => {
        if (!state.condition) return state;

        return {
          condition: {
            ...state.condition,
            item: item.item,
            bom: item.bom,
            processes: item.processes,
            bom_slots: item.bom_slots,
            item_changes: [],
            bom_changes: [],
            process_changes: [],
            item_replaced: true,
          },
        };
      });
    },
  })
);

// ============================================================================
// Reactive Hooks for Components
// ============================================================================

/**
 * Hook for using specification state with change tracking
 * Provides helpers for working with item changes and navigation
 */
export const useSpecificationState = (activeItem: ItemWithBOM | null) => {
  const condition = useSpecificationStateStore((state) => state.condition);
  const successMessage = useSpecificationStateStore(
    (state) => state.successMessage
  );
  const errorMessage = useSpecificationStateStore(
    (state) => state.errorMessage
  );
  const trackChange = useSpecificationStateStore((state) => state.trackChange);
  const removeChange = useSpecificationStateStore(
    (state) => state.removeChange
  );
  const clearChanges = useSpecificationStateStore(
    (state) => state.clearChanges
  );
  const appendBomItem = useSpecificationStateStore(
    (state) => state.appendBomItem
  );
  const appendProcess = useSpecificationStateStore(
    (state) => state.appendProcess
  );
  const replaceBomItem = useSpecificationStateStore(
    (state) => state.replaceBomItem
  );
  const updateConditionName = useSpecificationStateStore(
    (state) => state.updateConditionName
  );
  const checkForUnsavedChanges = useSpecificationStateStore(
    (state) => state.checkForUnsavedChanges
  );
  const isChanged = useSpecificationStateStore((state) => state.isChanged);
  const getChange = useSpecificationStateStore((state) => state.getChange);
  const setCondition = useSpecificationStateStore(
    (state) => state.setCondition
  );
  const setSuccessMessage = useSpecificationStateStore(
    (state) => state.setSuccessMessage
  );
  const clearSuccessMessage = useSpecificationStateStore(
    (state) => state.clearSuccessMessage
  );
  const setErrorMessage = useSpecificationStateStore(
    (state) => state.setErrorMessage
  );
  const clearErrorMessage = useSpecificationStateStore(
    (state) => state.clearErrorMessage
  );

  const hasUnsavedChanges = checkForUnsavedChanges();

  // Function to check and warn about unsaved changes before navigation
  const checkUnsavedChangesBeforeNavigation = (): boolean => {
    if (checkForUnsavedChanges()) {
      const proceed = window.confirm(
        "You have unsaved changes. Changing conditions will remove all in progress edits. Do you want to proceed?"
      );
      if (proceed) {
        clearChanges();
      }
      return proceed;
    }
    return true;
  };

  return {
    condition,
    hasUnsavedChanges,
    successMessage,
    errorMessage,
    trackChange,
    removeChange,
    clearChanges,
    appendBomItem,
    appendProcess,
    replaceBomItem,
    updateConditionName,
    isChanged,
    getChange,
    checkUnsavedChangesBeforeNavigation,
    setCondition,
    setSuccessMessage,
    clearSuccessMessage,
    setErrorMessage,
    clearErrorMessage,
  };
};

/**
 * Hook to check if there are unsaved changes (reactive)
 */
export const useHasUnsavedChanges = () => {
  return useSpecificationStateStore((state) => state.checkForUnsavedChanges());
};
